/**
 * File: AnalyticsChart.tsx
 * Path: apps/admin/src/features/dashboard/components/AnalyticsChart.tsx
 * Description: SVG-based line chart for displaying historical analytics trend data without external heavy visualization package overhead.
 */

import React from "react";
import type { ChartDataPoint } from "@/features/dashboard/utils/ga4Utils";

export interface AnalyticsChartProps {
  data: ChartDataPoint[];
  height?: number;
  isLoading?: boolean;
}

/**
 * Accessible SVG line chart to plot linear GA4 dimension metrics over custom durations.
 */
export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  data,
  height = 200,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div 
        style={{ height }} 
        className="w-full bg-card border border-border rounded-none flex items-center justify-center shadow-sm animate-pulse"
      >
        <div className="h-6 w-1/3 bg-muted rounded-none" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div 
        style={{ height }} 
        className="w-full bg-card border border-border rounded-none flex items-center justify-center shadow-sm text-muted-foreground text-sm"
      >
        No reporting data available for this range
      </div>
    );
  }

  const padding = 40;
  const chartWidth = 500;
  const chartHeight = height;

  const maxVal = Math.max(...data.map((d) => d.value), 1);
  const minVal = Math.min(...data.map((d) => d.value), 0);
  const valueRange = maxVal - minVal;

  // Map coordinates safely within the SVG viewing box boundary boundaries
  const points = data.map((d, index) => {
    const x = padding + (index / (data.length - 1 || 1)) * (chartWidth - padding * 2);
    const y = chartHeight - padding - ((d.value - minVal) / valueRange) * (chartHeight - padding * 2);
    return { x, y };
  });

  const pathD = points.reduce(
    (acc, p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`),
    "",
  );

  return (
    <div className="p-gutter-md bg-card border border-border rounded-none shadow-sm w-full">
      <svg 
        viewBox={`0 0 ${chartWidth} ${chartHeight}`} 
        className="w-full h-auto overflow-visible"
      >
        {/* Horizontal background helper grid line paths */}
        {[0, 0.5, 1].map((ratio, i) => {
          const y = padding + ratio * (chartHeight - padding * 2);
          const val = maxVal - ratio * valueRange;
          return (
            <g key={i} className="opacity-40 text-xs fill-muted-foreground">
              <line 
                x1={padding} 
                y1={y} 
                x2={chartWidth - padding} 
                y2={y} 
                className="stroke-border stroke-1 stroke-dasharray-[4,4]" 
              />
              <text x={padding - 5} y={y + 3} textAnchor="end">
                {Math.round(val)}
              </text>
            </g>
          );
        })}

        {/* Linear geometric trends mapping path block */}
        {points.length > 1 && (
          <path
            d={pathD}
            fill="none"
            className="stroke-primary stroke-2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {/* Interactive point indicators and grid placement mapping */}
        {points.map((p, i) => (
          <g key={i} className="group/point">
            <circle
              cx={p.x}
              cy={p.y}
              r={3}
              className="fill-background stroke-primary stroke-2 hover:r-5 transition-all duration-150 cursor-pointer"
            />
            {data.length <= 15 && i % Math.ceil(data.length / 5) === 0 && (
              <text
                x={p.x}
                y={chartHeight - padding + 15}
                textAnchor="middle"
                className="text-xs fill-muted-foreground opacity-80"
              >
                {data[i].label}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};